import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { TripLegType } from '@/data/models/reservation.model';

import {
  emptyTripSegment,
  type ReservationFormInput,
} from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';

const PREFIX = 'TripTypeField';

const classes = {
  toggleContainer: `${PREFIX}-toggleContainer`,
  helperText: `${PREFIX}-helperText`,
  helperTextContainer: `${PREFIX}-helperTextContainer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.toggleContainer}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
  [`& .${classes.helperText}`]: {
    marginTop: theme.spacing(0),
  },
  [`& .${classes.helperTextContainer}`]: {
    marginTop: theme.spacing(2),
  },
}));

export function TripTypeField() {
  const { t } = useTranslation('common');
  const { control, getValues, setValue } =
    useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs.tripType;

  const OPTIONS = [
    {
      label: t('member.oneWay'),
      value: TripLegType.OneWay,
      dataFsElement: 'OneWayReservationButton',
    },
    {
      label: t('member.roundTrip'),
      value: TripLegType.RoundTrip,
      dataFsElement: 'RoundTripReservationButton',
    },
    {
      label: t('member.multiTrip'),
      value: TripLegType.MultiLeg,
      dataFsElement: 'MultiTripReservationButton',
    },
  ];

  return (
    <StyledBox>
      <Controller
        name="tripType"
        control={control}
        render={({ field }) => (
          <FormControl component="fieldset" variant="standard" fullWidth>
            <ToggleButtonGroup
              className={classes.toggleContainer}
              exclusive
              color="primary"
              value={field.value}
              onChange={(_e, next: TripLegType | null) => {
                if (next === null) return;
                field.onChange(next);
                actor.send({ type: 'CHANGE', value: next });
                const legs = getValues('trips');
                if (next !== TripLegType.MultiLeg && legs.length > 1) {
                  setValue(
                    'trips',
                    [
                      {
                        ...(legs[0] ?? emptyTripSegment),
                        tripLegType: next,
                      },
                    ],
                    { shouldValidate: true },
                  );
                } else {
                  setValue(
                    'trips',
                    legs.map((leg) => ({ ...leg, tripLegType: next })),
                    { shouldValidate: true },
                  );
                }
              }}
              onBlur={field.onBlur}
              aria-label={t('member.tripType', { defaultValue: 'Trip type' })}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}>
              {OPTIONS.map(({ label, value, dataFsElement }) => (
                <ToggleButton
                  key={value}
                  value={value}
                  data-fs-element={dataFsElement}
                  sx={{ flex: 1, textTransform: 'none' }}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {field.value && (
              <Box className={classes.helperTextContainer}>
                <FormHelperText className={classes.helperText}>
                  {field.value === TripLegType.OneWay &&
                    t(t('member.oneWayHelperText'))}
                  {field.value === TripLegType.RoundTrip &&
                    t('member.roundTripHelperText')}
                  {field.value === TripLegType.MultiLeg &&
                    t('member.multiLegHelperText')}
                </FormHelperText>
              </Box>
            )}
          </FormControl>
        )}
      />
    </StyledBox>
  );
}
